// V1 — modern news (NYT / Bloomberg redesign). Magazine mosaic: rotating row patterns
// (3-wide / hero+list / 6-compact) so the page reads in varied rhythms, not a uniform grid.
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { ARTICLES, SECTIONS, AUTHORS, TICKER, getAuthor, articlesBySection } from "@/lib/news";
import MarketsModule from "./MarketsModule";

const Byline = ({ slug, date, read }) => {
  const a = getAuthor(slug);
  return (
    <p className="text-[11px] flex items-center gap-1.5 text-[var(--fg-mute)]">
      <img src={a.img} alt="" className="w-4 h-4 rounded-full object-cover"/>
      <span><span className="font-display text-[var(--fg)]">{a.name}</span> · {date} · <span style={{color:"var(--accent)"}}>{a.read || ""}</span></span>
    </p>
  );
};

// === CARD VARIANTS ===

// Wide card — used in 3-up rows
const WideCard = ({ a, color = "" }) => (
  <article className="flex flex-col h-full" data-testid={`v1-card-${a.slug}`}>
    <Link to={`/article/${a.slug}`} className="block aspect-[4/3] overflow-hidden mb-2 hover-lift">
      <img src={a.cover} alt={a.coverAlt} className="w-full h-full object-cover"/>
    </Link>
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className={`pill-section ${color}`}>{a.subtag}</span>
      {a.premium && <span className="premium-tag">★ Subscriber</span>}
    </div>
    <h3 className="text-headline text-base lg:text-lg mt-1.5 leading-snug min-h-[3.2rem]">
      <Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link>
    </h3>
    <p className="font-italic text-[13px] text-[var(--fg-mute)] mt-1.5 line-clamp-3 min-h-[3.4rem]">{a.dek}</p>
    <div className="mt-auto pt-2"><Byline slug={a.author} date={a.date} read={a.read}/></div>
  </article>
);

// Compact card — used in 6-across rails
const CompactCard = ({ a, color = "" }) => (
  <article className="flex flex-col h-full" data-testid={`v1-card-${a.slug}`}>
    <Link to={`/article/${a.slug}`} className="block aspect-[16/10] overflow-hidden mb-1.5 hover-lift">
      <img src={a.cover} alt={a.coverAlt} className="w-full h-full object-cover"/>
    </Link>
    <span className={`pill-section ${color} self-start`}>{a.subtag}</span>
    <h3 className="text-headline text-[13px] mt-1 leading-tight line-clamp-3 min-h-[3.2rem]">
      <Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link>
    </h3>
    <p className="text-[10px] text-[var(--fg-mute)] mt-auto pt-1 flex items-center gap-1"><Clock size={9}/>{a.read} · {a.date}</p>
  </article>
);

// Hero feature — large card used as the anchor on the left of an asymmetric row
const HeroFeature = ({ a, color = "" }) => (
  <article className="flex flex-col" data-testid={`v1-card-${a.slug}`}>
    <Link to={`/article/${a.slug}`} className="block aspect-[16/10] overflow-hidden mb-3 hover-lift">
      <img src={a.cover} alt={a.coverAlt} className="w-full h-full object-cover"/>
    </Link>
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className={`pill-section ${color}`}>{a.subtag}</span>
      {a.premium && <span className="premium-tag">★ Subscriber</span>}
    </div>
    <h3 className="text-headline text-xl lg:text-3xl mt-2 leading-[1.05]">
      <Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link>
    </h3>
    <p className="font-italic text-sm lg:text-base text-[var(--fg-mute)] mt-2 line-clamp-3">{a.dek}</p>
    <div className="mt-2"><Byline slug={a.author} date={a.date} read={a.read}/></div>
  </article>
);

// List item — small horizontal card used in a vertical list next to a hero
const ListItem = ({ a, color = "", divide = true }) => (
  <article className={`grid grid-cols-12 gap-3 py-3 ${divide ? "border-b last:border-0" : ""}`} style={{borderColor:"var(--line-soft)"}} data-testid={`v1-card-${a.slug}`}>
    <Link to={`/article/${a.slug}`} className="col-span-4 aspect-[4/3] overflow-hidden hover-lift">
      <img src={a.cover} alt="" className="w-full h-full object-cover"/>
    </Link>
    <div className="col-span-8 flex flex-col">
      <span className={`pill-section ${color} self-start`}>{a.subtag}</span>
      <h3 className="text-headline text-sm mt-1 leading-snug line-clamp-3">
        <Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link>
      </h3>
      <p className="text-[10px] text-[var(--fg-mute)] mt-auto pt-1 flex items-center gap-1"><Clock size={9}/>{a.read} · {a.date}</p>
    </div>
  </article>
);

// === SECTION HEADER ===

const SectionHeader = ({ name, slug, count, accent = "var(--accent)" }) => (
  <div className="rule-section mb-4 flex items-baseline justify-between flex-wrap gap-2">
    <div className="flex items-baseline gap-3">
      <p className="text-[10px] smcaps" style={{color:accent}}>{name}</p>
      <span className="text-[10px] font-mono text-[var(--fg-mute)]">{count} stories</span>
    </div>
    <Link to={`/section/${slug}`} className="text-[11px] font-cond" style={{color:accent}}>All {name.toLowerCase()} →</Link>
  </div>
);

// === SECTION LAYOUT VARIANTS ===

// Pattern B — hero on the left + 2 horizontal cards underneath + a packed list on the right.
// Both columns always filled with article cards (no widgets, just more articles).
const PatternHeroList = ({ list, color, extras }) => {
  if (list.length === 0) return null;
  const [hero, ...rest] = list;
  const all = [...rest, ...(extras || [])];
  const leftBelow  = all.slice(0, 2);    // 2 cards under the hero
  const rightItems = all.slice(2, 7);    // 5 cards stacked on the right
  return (
    <div className="grid grid-cols-12 gap-x-5 gap-y-5">
      <div className="col-span-12 lg:col-span-7 lg:border-r lg:pr-6 flex flex-col" style={{borderColor:"var(--line-soft)"}}>
        <HeroFeature a={hero} color={color}/>
        <div className="mt-5 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 gap-x-4" style={{borderColor:"var(--line-soft)"}}>
          {leftBelow.map(a => <ListItem key={a.slug} a={a} color="pill-section-blue"/>)}
        </div>
      </div>
      <div className="col-span-12 lg:col-span-5 flex flex-col">
        <p className="text-[10px] smcaps mb-1" style={{color:"var(--accent2)"}}>More to read</p>
        <div className="rule-thin"/>
        {rightItems.map(a => <ListItem key={a.slug} a={a} color="pill-section-blue"/>)}
      </div>
    </div>
  );
};

// Pattern C — six compact cards in a row (filled with extras if section is short)
const PatternCompact6 = ({ list, color, extras }) => {
  const filler = (extras || []).slice(0, Math.max(0, 6 - list.length));
  const cards = [...list.slice(0, 6), ...filler].slice(0, 6);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-5">
      {cards.map(a => <CompactCard key={a.slug} a={a} color={color}/>)}
    </div>
  );
};

// Pattern A — three wide cards (filled with extras if section is short)
const PatternWide3 = ({ list, color, extras }) => {
  const filler = (extras || []).slice(0, Math.max(0, 3 - list.length));
  const cards = [...list.slice(0, 3), ...filler].slice(0, 3);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-7">
      {cards.map(a => <WideCard key={a.slug} a={a} color={color}/>)}
    </div>
  );
};

// Map a section index to a pattern. Organized rotation: A → B → C → A → B → A → C
const PATTERNS = [PatternWide3, PatternHeroList, PatternCompact6, PatternWide3, PatternHeroList, PatternWide3, PatternCompact6];
const COLORS   = ["", "pill-section-blue", "pill-section-gold", "", "pill-section-blue", "", "pill-section-gold"];

export default function NewsV1Home() {
  const lead = ARTICLES.find(a => a.featured) || ARTICLES[0];
  // Latest = mosaic of 6 compact + 3 wide = 9 stories, magazine rhythm.
  const latest = ARTICLES.filter(a => a.slug !== lead.slug).slice(0, 9);
  const latestRow1 = latest.slice(0, 6);
  const latestRow2 = latest.slice(6, 9);

  return (
    <div data-testid="newsv1-home">
      {/* HERO LEAD MODULE */}
      <section className="border-b px-4 lg:px-8 py-6" style={{borderColor:"var(--line)"}}>
        <div className="grid grid-cols-12 gap-x-5 gap-y-5">
          <aside className="hidden lg:block lg:col-span-3 border-r pr-4" style={{borderColor:"var(--line-soft)"}}>
            <p className="text-[10px] smcaps mb-2 rule-section" style={{color:"var(--accent)"}}>Top stories</p>
            <ul className="space-y-3">
              {ARTICLES.slice(1, 21).map((a, i) => (
                <li key={a.slug} className="grid grid-cols-12 gap-2 pb-3 border-b last:border-0" style={{borderColor:"var(--line-soft)"}}>
                  <Link to={`/article/${a.slug}`} className="col-span-4 aspect-square overflow-hidden block hover-lift"><img src={a.cover} alt="" className="w-full h-full object-cover"/></Link>
                  <div className="col-span-8">
                    <span className="pill-section">No. {String(i+2).padStart(2,"0")} · {a.tag}</span>
                    <h3 className="font-display text-[13px] mt-1 leading-tight"><Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link></h3>
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          <div className="col-span-12 lg:col-span-6">
            <div className="rule-section flex items-baseline justify-between">
              <p className="text-[10px] smcaps" style={{color:"var(--accent)"}}>Lead · {lead.tag} · {lead.subtag}</p>
              {lead.premium && <span className="premium-tag">★ Subscriber</span>}
            </div>
            <h1 className="text-headline text-3xl sm:text-4xl lg:text-6xl mt-3 mb-2"><Link to={`/article/${lead.slug}`} className="underline-on-hover">{lead.title}</Link></h1>
            <p className="font-italic text-base lg:text-xl text-[var(--fg-mute)] leading-snug mb-3">{lead.dek}</p>
            <Link to={`/article/${lead.slug}`} className="block aspect-[16/9] overflow-hidden mb-2"><img src={lead.cover} alt={lead.coverAlt} className="w-full h-full object-cover"/></Link>
            <p className="text-[10px] text-[var(--fg-mute)] italic mb-3">{lead.coverAlt}</p>
            <Byline slug={lead.author} date={lead.date} read={lead.read}/>
            <p className="dropcap text-sm leading-relaxed mt-4">{lead.body[0].text}</p>
            <Link to={`/article/${lead.slug}`} className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-cond" style={{color:"var(--accent)"}}>Continue reading <ArrowUpRight size={12}/></Link>

            {/* Fill remaining vertical space with post previews (matches sidebar heights) */}
            <div className="mt-6 pt-5 border-t" style={{borderColor:"var(--line-soft)"}}>
              <p className="text-[10px] smcaps mb-4 rule-section" style={{color:"var(--accent)"}}>Also today</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
                {ARTICLES.filter(a => a.slug !== lead.slug && !ARTICLES.slice(1,6).some(x => x.slug === a.slug)).slice(0, 10).map(a => (
                  <article key={a.slug} className="flex flex-col">
                    <Link to={`/article/${a.slug}`} className="block aspect-[4/3] overflow-hidden mb-2 hover-lift"><img src={a.cover} alt={a.coverAlt} className="w-full h-full object-cover"/></Link>
                    <span className="pill-section self-start">{a.tag} · {a.subtag}</span>
                    <h3 className="font-display text-[15px] mt-1.5 leading-snug"><Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link></h3>
                    <p className="font-italic text-[12px] text-[var(--fg-mute)] mt-1 line-clamp-2">{a.dek}</p>
                    <p className="text-[10px] text-[var(--fg-mute)] mt-1.5 flex items-center gap-1"><Clock size={9}/>{a.read} · {getAuthor(a.author).name.split(" ")[0]}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>

          <aside className="col-span-12 lg:col-span-3 lg:border-l lg:pl-4" style={{borderColor:"var(--line-soft)"}}>
            <MarketsModule/>
            <p className="text-[10px] smcaps mb-2 mt-5 rule-section" style={{color:"var(--accent2)"}}>Editor's picks</p>
            <ul className="space-y-3">
              {ARTICLES.filter(a=>a.featured || a.premium).slice(0,7).map(a => (
                <li key={a.slug} className="pb-3 border-b last:border-0" style={{borderColor:"var(--line-soft)"}}>
                  <Link to={`/article/${a.slug}`} className="block aspect-[5/3] overflow-hidden mb-1.5 hover-lift"><img src={a.cover} alt="" className="w-full h-full object-cover"/></Link>
                  <span className="pill-section pill-section-blue">{a.tag}</span>
                  <h3 className="font-display text-[13px] mt-1 leading-tight"><Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link></h3>
                  <p className="text-[10px] text-[var(--fg-mute)] mt-1 flex items-center gap-1"><Clock size={9}/>{a.read}</p>
                </li>
              ))}
            </ul>
            <div className="mt-3 p-3 border" style={{borderColor:"var(--accent)", background:"var(--bg-elev)"}}>
              <p className="font-cond text-[10px] mb-1" style={{color:"var(--accent)"}}>Subscribe</p>
              <p className="font-display text-base leading-tight">Today's edition, every morning, in your inbox.</p>
              <Link to="/contact" className="font-cond text-[10px] mt-2 inline-flex items-center gap-1" style={{color:"var(--accent)"}}>Reserve a seat →</Link>
            </div>

            {/* READER PICKS — compact list, keeps the right column packed */}
            <p className="text-[10px] smcaps mt-5 mb-2 rule-section" style={{color:"var(--accent)"}}>Reader picks</p>
            <ul className="space-y-2.5">
              {ARTICLES.slice(-10, -2).map((a, i) => (
                <li key={a.slug} className="pb-2.5 border-b last:border-0" style={{borderColor:"var(--line-soft)"}}>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-lg leading-none shrink-0" style={{color:"var(--accent)"}}>{i+1}</span>
                    <div>
                      <span className="text-[9px] font-mono uppercase tracking-wider" style={{color:"var(--accent2)"}}>{a.tag} · {a.subtag}</span>
                      <h3 className="font-display text-[12.5px] leading-tight mt-0.5"><Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link></h3>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </section>

      {/* LATEST — magazine mosaic: 6 compact + 3 wide */}
      <section className="border-b px-4 lg:px-8 py-7" style={{borderColor:"var(--line)", background:"var(--bg-elev)"}}>
        <div className="rule-section mb-4 flex items-baseline justify-between flex-wrap gap-2">
          <div className="flex items-baseline gap-3">
            <p className="text-[10px] smcaps" style={{color:"var(--accent)"}}>Latest · {latest.length} stories</p>
            <span className="text-[10px] font-mono text-[var(--fg-mute)]">updated continuously</span>
          </div>
          <Link to="/section/politics" className="text-[11px] font-cond" style={{color:"var(--accent)"}}>Browse all sections →</Link>
        </div>

        {/* Row 1 — 6 compact across */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-5 pb-6 mb-6 border-b" style={{borderColor:"var(--line-soft)"}}>
          {latestRow1.map((a, i) => <CompactCard key={a.slug} a={a} color={i%3===1?"pill-section-blue":i%3===2?"pill-section-gold":""}/>)}
        </div>

        {/* Row 2 — 3 wide */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-7">
          {latestRow2.map((a, i) => <WideCard key={a.slug} a={a} color={i===1?"pill-section-blue":""}/>)}
        </div>
      </section>

      {/* SECTION LOOP — rotating mosaic patterns; empty cells get filled with extra articles */}
      {SECTIONS.map((s, idx) => {
        const list = articlesBySection(s.slug);
        if (list.length === 0) return null;
        const Pattern = PATTERNS[idx % PATTERNS.length];
        const color = COLORS[idx % COLORS.length];
        const accent = idx % 3 === 1 ? "var(--accent2)" : idx % 3 === 2 ? "var(--accent3)" : "var(--accent)";
        // extras = cross-section articles to fill empty grid cells (same card format as the row)
        const usedInSection = new Set(list.map(a => a.slug));
        const extras = ARTICLES.filter(a => !usedInSection.has(a.slug) && a.slug !== lead.slug);
        return (
          <section key={s.slug} className="border-b px-4 lg:px-8 py-7" style={{borderColor:"var(--line)"}}>
            <SectionHeader name={s.name} slug={s.slug} count={list.length} accent={accent}/>
            <Pattern list={list} color={color} extras={extras}/>
          </section>
        );
      })}

      {/* MORNING BRIEFING — 3 uniform cards (kept editorial) */}
      <section className="border-b px-4 lg:px-8 py-7" style={{borderColor:"var(--line)", background:"var(--bg-elev)"}}>
        <div className="rule-section mb-4"><p className="text-[10px] smcaps" style={{color:"var(--accent)"}}>This morning's briefing</p></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-3 col-rule">
          {TICKER.slice(0,3).map((t, i) => (
            <article key={i} className="md:pl-5 first:md:pl-0">
              <span className="pill-section pill-section-blue">Brief · {String(i+1).padStart(2,"0")}</span>
              <h3 className="font-display text-base mt-1.5 leading-snug">{t}</h3>
              <p className="text-xs text-[var(--fg-mute)] mt-1.5 leading-relaxed">{ARTICLES[i].dek}</p>
            </article>
          ))}
        </div>
      </section>

      {/* AUTHORS */}
      <section className="border-b px-4 lg:px-8 py-6" style={{borderColor:"var(--line)"}}>
        <div className="rule-section mb-3"><p className="text-[10px] smcaps" style={{color:"var(--accent)"}}>Today's bylines</p></div>
        <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
          {AUTHORS.map(a => (
            <div key={a.slug} className="text-center">
              <div className="aspect-square overflow-hidden mb-1.5 rounded-full border-2 mx-auto w-16 h-16" style={{borderColor:"var(--accent)"}}><img src={a.img} alt={a.name} className="w-full h-full object-cover"/></div>
              <p className="font-display text-[12px]">{a.name}</p>
              <p className="text-[10px] text-[var(--fg-mute)]">{a.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SUBSCRIBE CTA */}
      <section className="px-4 lg:px-8 py-10" style={{background:"var(--accent)", color:"var(--accent-fg)"}}>
        <div className="max-w-3xl">
          <p className="font-cond text-[11px] mb-2 opacity-80">Daily edition · early access · exclusive Subscriber pieces</p>
          <h2 className="text-headline text-3xl lg:text-5xl">Today's edition, in your inbox <span className="font-italic">every morning</span>.</h2>
          <Link to="/contact" className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 bg-white text-black font-cond text-xs hover:opacity-90">Subscribe — A$3 / month <ArrowUpRight size={12}/></Link>
        </div>
      </section>
    </div>
  );
}
